import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api, { setAuthToken, getAuthToken, apiErrorMessage } from '@/lib/api';
import { ShieldCheck } from 'lucide-react';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  // If already an admin, jump straight to dashboard
  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;
    api.get('/auth/me')
      .then(r => { if (r.data?.role === 'admin') navigate('/admin/dashboard'); })
      .catch(() => {});
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      if (res.data?.role !== 'admin') {
        toast.error('This sign-in is restricted to administrators.');
        return;
      }
      if (res.data.token) setAuthToken(res.data.token);
      navigate('/admin/dashboard');
    } catch (err) {
      // Cold-start (5xx / network) → "Server warming up" copy from
      // apiErrorMessage. Real 401 → backend-supplied "Invalid email or
      // password" detail. Prevents the misleading "bad password" toast
      // when the backend is actually just booting.
      toast.error(apiErrorMessage(err, 'Invalid email or password'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      toast.success("If an admin account exists for that email, we've sent a reset link.");
      setShowForgot(false);
      setForgotEmail('');
    } catch (err) {
      // Endpoint always 200s by design — this is a defensive fallback only
      toast.success("If an admin account exists for that email, we've sent a reset link.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4" data-testid="admin-login-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-amber-400/10 mb-3">
            <ShieldCheck className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="text-3xl font-bold uppercase tracking-tight text-white">Admin Console</h1>
          <p className="text-sm text-slate-400 mt-1">SmarterHoops administrator sign-in</p>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-md p-8">
          {!showForgot ? (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label htmlFor="admin-email" className="text-xs font-bold uppercase tracking-wider text-slate-300 mb-2 block">
                  Email
                </Label>
                <Input
                  id="admin-email"
                  type="email"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 bg-slate-900 text-white border-slate-700"
                  data-testid="admin-email-input"
                />
              </div>
              <div>
                <Label htmlFor="admin-password" className="text-xs font-bold uppercase tracking-wider text-slate-300 mb-2 block">
                  Password
                </Label>
                <Input
                  id="admin-password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 bg-slate-900 text-white border-slate-700"
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="admin-login-button"
                className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold uppercase tracking-wide"
              >
                {loading ? 'Signing in…' : 'Sign In'}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setForgotEmail(email); setShowForgot(true); }}
                  className="text-xs text-slate-400 hover:text-amber-400 transition-colors"
                  data-testid="admin-forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleForgot} className="space-y-5" data-testid="admin-forgot-form">
              <p className="text-sm text-slate-300">
                Enter your admin email. If an account exists, we'll send a reset link.
              </p>
              <Input
                id="forgot-email"
                type="email"
                required
                autoComplete="username"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="admin@smarterhoops.com"
                className="h-12 bg-slate-900 text-white border-slate-700"
                data-testid="admin-forgot-email-input"
              />
              <Button
                type="submit"
                disabled={forgotLoading}
                data-testid="admin-forgot-submit-button"
                className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold uppercase tracking-wide"
              >
                {forgotLoading ? 'Sending…' : 'Send Reset Link'}
              </Button>
              <div className="text-center">
                <button type="button" onClick={() => setShowForgot(false)}
                  className="text-xs text-slate-400 hover:text-amber-400 transition-colors">
                  ← Back to sign in
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="mt-6 text-center">
          <a
            href="https://apps.apple.com/app/smarterhoops"
            className="text-xs text-slate-500 hover:text-slate-300"
          >
            Not an admin? Get the app
          </a>
        </div>
      </div>
    </div>
  );
}
